import * as THREE from 'three';
import { G, M, TINT, HP, glowMat, mesh, bevel, plate, rib, seam, mergeBones, damp, boneG } from './kit';
/* ============================================================
   KATHEDRALE 大聖堂 —— BOSS・移動要塞
   ============================================================

   剪影：三片巨牆＋履帶座
   二階段：三牆外翻・核心露出（橙→紫）

   由 `tools/port-bosses.mjs` 從 `enemy_sample_ani.html` 機械移植，
   **不要手改這個檔**：要改造型請改原型再重跑移植器，否則 1:1 就斷了。
   驗證：`node tools/port-bosses.mjs --check`。
   ⚠️ 比對只守得住數字與字串，**動畫對不對只能實看**（圖鑑的開發模式）。 */


/* ── BOSS-03 KATHEDRALE 大聖堂 ── WALL 家族推到極限：一面會走路的牆。
   P1 牆閉合，只有牆上的砲口在射（橙・直射）→ 找掩體。
   P2 三片牆整個外翻，核心露出來（紫・範圍）→ 掩體沒了，改成走位。 */
export function buildKathedrale(){
  var GLA = glowMat(TINT.amb.hex, 2.0), GLV = glowMat(TINT.vio.hex, 2.2);
  var root = new THREE.Group(), i, j;
  var base = boneG(root, 0, 0.92, 0);
  base.add(bevel(M.dark,  8.40, 1.48, 4.20, [0,0,0]));
  base.add(plate(M.armor, 8.80, 0.72, 1.90, [0, 0.46, 1.10], -0.30));
  base.add(plate(M.armor, 8.60, 0.62, 1.70, [0, 0.42,-1.16],  0.26));
  base.add(rib  (M.armor, 8.90, 0.14, 0.14, [0, 0.86, 0.52], -0.30));
  for (i = 0; i < 6; i++){
    var wx = -3.5 + i*1.40;
    base.add(mesh(G.cyl, M.trim, [0.86,0.36,0.86], [wx,-0.62, 1.86], [0,0,HP]));
    base.add(mesh(G.cyl, M.trim, [0.86,0.36,0.86], [wx,-0.62,-1.86], [0,0,HP]));
  }
  base.add(seam(7.60, 0.10, 0.12, [0, 0.10, 2.10], -0.30));

  /* 三片巨牆：樞紐放在各自的內緣，P2 才翻得開 */
  var walls = [];
  for (i = 0; i < 3; i++){
    var hx = (i - 1) * 3.30;
    var w = boneG(base, hx + (i === 0 ? -1.55 : (i === 2 ? 1.55 : 0)), 0.74, 0.30);
    var ox = (i === 0 ? 1.55 : (i === 2 ? -1.55 : 0));
    w.add(plate(M.armor, 3.10, 5.90, 0.44, [ox, 2.95, 0], 0, 0, 0));
    w.add(rib  (M.armor, 0.16, 5.60, 0.16, [ox-1.42, 2.95, 0.26]));
    w.add(rib  (M.armor, 0.16, 5.60, 0.16, [ox+1.42, 2.95, 0.26]));
    w.add(plate(M.dark,  2.70, 0.34, 0.30, [ox, 5.72, 0.10], -0.34));
    for (j = 0; j < 3; j++){
      w.add(mesh(G.cyl, M.trim, [0.44,0.52,0.44], [ox-0.90+j*0.90, 1.30+j*1.30, 0.32], [HP,0,0]));
      var pr = mesh(G.tor, GLA, [0.56,0.56,0.55], [ox-0.90+j*0.90, 1.30+j*1.30, 0.56]);
      pr.userData.noMerge = 1; w.add(pr);
    }
    walls.push(w);
  }
  /* 核心：P1 藏在牆後，P2 才看得到 —— 造型講機制，不用 UI 教 */
  var core = boneG(base, 0, 3.40, -0.70);
  var cm = mesh(G.oct, GLV, [1.90,1.90,1.90], [0,0,0]); cm.userData.noMerge = 1; core.add(cm);
  core.add(mesh(G.tor, M.trim, [2.70,2.70,0.40], [0,0,0], [HP,0,0]));
  core.add(mesh(G.tor, M.trim, [2.50,2.50,0.40], [0,0,0], [0,HP,0]));
  base.add(mesh(G.cone, M.armor, [1.20,2.20,1.20], [0, 6.90,-0.70]));
  base.add(mesh(G.cone, M.trim,  [0.34,1.10,0.34], [0, 8.20,-0.70]));

  root.userData.rig = {base:base, walls:walls, core:core, GLA:GLA, GLV:GLV, cm:cm};
  return mergeBones(root);
}
export function updKathedrale(rig, o, dt){
  var open = o.p2;
  rig.op = damp(rig.op === undefined ? 0 : rig.op, open ? 1 : 0, 3.2, dt);
  var op = rig.op;
  rig.walls[0].rotation.y = -1.22*op;
  rig.walls[2].rotation.y =  1.22*op;
  rig.walls[1].position.z = 0.30 + 2.30*op;
  rig.walls[1].position.y = 0.74 - 0.42*op;
  rig.core.position.y = 3.40 + 0.70*op + 0.10*Math.sin(o.t*1.4);
  var k = 0.35 + 1.05*op;
  rig.cm.scale.setScalar(k*(1 + 0.10*Math.sin(o.t*3.2) + 0.45*o.tel));
  rig.GLA.emissiveIntensity = 2.0*(1 - 0.7*op) + 3.4*o.tel + 5*o.atk*(1-o.atk);
  rig.GLV.emissiveIntensity = 0.6 + 2.4*op + 3.0*o.tel*op;
  rig.base.position.y = 0.92 + 0.05*Math.sin(o.ph*2*Math.PI * 2)*o.mv;
  rig.base.rotation.x = 0.03*Math.sin(o.ph*Math.PI * 2)*o.mv - o.tel*0.04;
}